import { catchError, map, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { DualPanelItem } from '../../../interfaces/DualPanelItem.interface';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../environment/environment';

type ModuleDescriptionMap = Record<string, { translation_text?: string }>;
type ModuleWithVersions = {
    module_id?: number | string;
    name?: string;
    visibility?: string;
    versions?: Array<{ descriptions?: ModuleDescriptionMap }>;
};

function getModuleDescription(module: ModuleWithVersions): string {
    const versions = module.versions;
    if (!versions?.length) return '';

    const descriptions = versions[0]?.descriptions;
    if (!descriptions || typeof descriptions !== 'object') return '';

    for (const lang of ['da', '0', 'en']) {
        const text = descriptions[lang]?.translation_text?.trim();
        if (text) return text;
    }

    for (const desc of Object.values(descriptions)) {
        const text = desc?.translation_text?.trim();
        if (text) return text;
    }

    return '';
}

function mapModuleToDualPanelItem(module: ModuleWithVersions & { module_id: number | string; name: string }): DualPanelItem {
    return {
        value: module.module_id,
        name: module.name,
        description: getModuleDescription(module)
    };
}

export function dual_panel_modules(componentDataSourceService: ComponentDataSourceService) {
    return {
        name: 'Dual Panel Modules',
        description: 'Lists all modules in a format suitable for dual panel selector',
        execute: (params?: LoadDataParams) => {

            if (!params) params = { dataSourceKey: 'dual-panel-modules', queryParams: {} };
            if (!params.queryParams) params.queryParams = {};
            
            // Check if this is a request for specific IDs (for selected items)
            const hasIdsInQueryParams = params.queryParams['ids'] !== undefined;
            const hasIdsInFilters = params.filters && params.filters.some(f => f.column === 'ids');
            const hasIds = hasIdsInQueryParams || hasIdsInFilters;
            
            // Special checks for explicit by-ids flags
            const forceByIds = params.queryParams['forceByIds'] === true || 
                              params.queryParams['useByIdsEndpoint'] === true ||
                              params.queryParams['_byIds'] === 'true';
            
            const specialPath = (params as any).specialPath === 'by-ids'; 
            
            
            if (hasIds || forceByIds || specialPath) {
                
                // Extract the IDs from either queryParams or filters
                let selectedIds;
                if (params.queryParams['ids']) {
                    // If ids is a string, split it into an array
                    selectedIds = typeof params.queryParams['ids'] === 'string' 
                        ? params.queryParams['ids'].split(',').map(id => id.trim()) 
                        : params.queryParams['ids'];
                } else if (params.filters) {
                    // Find the ids filter
                    const idsFilter = params.filters.find(f => f.column === 'ids');
                    if (idsFilter && idsFilter.value) {
                        selectedIds = idsFilter.value;
                    }
                }
                
                // If we have IDs, make sure they're included in the API request
                if (selectedIds && selectedIds.length > 0) {
                    
                    // For clarity, remove pagination parameters when fetching by IDs
                    if (params.queryParams['pageSize']) {
                        delete params.queryParams['pageSize'];
                    }
                    if (params.queryParams['pageIndex']) {
                        delete params.queryParams['pageIndex'];
                    }
                    
                    // Add the IDs to the request
                    params.queryParams['ids'] = Array.isArray(selectedIds) 
                        ? selectedIds.join(',') 
                        : selectedIds;
                    
                    // Set a flag to indicate this is a special request for selected IDs
                    params.queryParams['includeSelectedIds'] = true;
                    
                    
                    // Use the by-ids endpoint explicitly
                    const byIdsUrl = `${environment.apiUrl}/events/modules/by-ids`;
                    
                    // Make the request to the by-ids endpoint
                    return componentDataSourceService.http.get<any>(
                        byIdsUrl,
                        { params: params.queryParams }
                    ).pipe(
                        catchError(error => {
                            console.error('[Modules] Error in by-ids endpoint:', error);
                            
                            // If the by-ids endpoint fails, fall back to the regular endpoint
                            const regularUrl = `${environment.apiUrl}/events/modules`;
                            
                            return componentDataSourceService.http.get<any>(
                                regularUrl,
                                { params: params.queryParams }
                            ).pipe(
                                catchError(fallbackError => {
                                    console.error('[Modules] Error in fallback fetch:', fallbackError);
                                    return of({ data: [] });
                                })
                            );
                        }),
                        map(result => {
                            if (result && result.items) {
                                try {
                                    // Map the modules to DualPanelItem format
                                    const items: DualPanelItem[] = result.items.map((module: any) => mapModuleToDualPanelItem(module));
                                    
                                    return { data: items };
                                } catch (error) {
                                    console.error('[Modules] Error processing module data:', error);
                                    return { data: [] };
                                }
                            } else {
                                console.warn('[Modules] No specific modules found for the requested IDs');
                                return { data: [] };
                            }
                        })
                    );
                }
            }
            
            // For regular requests (no specific IDs), use standard pagination
            params.queryParams['pageSize'] = params.queryParams['pageSize'] || -1;
            
            return componentDataSourceService.http.get<any>(
                `${environment.apiUrl}/events/modules`,
                params?.queryParams ? { params: params.queryParams } : {}
            ).pipe(
                catchError(error => {
                    console.error('[Modules] Error fetching modules for dual panel:', error);
                    return of({ data: [] });
                }),
                map(result => {
                    if (result && result.items) {
                        try {
                            // Map the modules to DualPanelItem format
                            const items: DualPanelItem[] = result.items.map((module: any) => mapModuleToDualPanelItem(module));
                            
                            return {
                                data: items // Wrap in data property as expected by dual-panel-selector
                            };
                        } catch (error) {
                            console.error('[Modules] Error converting modules to DualPanelItem format:', error);
                            return { data: [] };
                        }
                    } else {
                        console.warn('[Modules] No modules found');
                        return { data: [] };
                    }
                })
            );
        }
    };
} 